/* The Atelier: warm-up queue (layout/theme.liquid, deferred, every page).
   The first screen loads first and alone (preloads, eager photos, fetchpriority high). Once it is done and the page is
   idle, this fetches the page's remaining lazy photos in a deliberate order, one or two at a time, so a shopper who
   lingers and then swipes or scrolls finds them ready instead of a grey box.

   When: once the whole document has arrived (DOMContentLoaded), the largest element has painted and, if it is a photo,
   it has landed (or at window load, if that comes first; on phones third-party scripts hold load back for seconds),
   in an idle callback (a microtask where requestIdleCallback is missing), and only while no photo on the screen is
   still arriving: the first screen, and later wherever she has scrolled or swiped to, always comes first; the queue
   moves on when the last of those lands. One photo at a time until window load (so load, where the store's app
   scripts start, waits for one photo at most), two after; each next one starts from an idle callback when a slot
   frees (its load or error). No timers.
   Order: (0) product gallery photos in gallery order (hero slides included; photos behind "Show all" go to tier 2);
   (1) everything else, nearest to the screen first (down or across, so the next rail's first card comes before this
   rail's fifth), which reads top to bottom: 'Pairs well with' rows, customer review photos (the uncropped copy
   at-reviews.js swaps in), the recommendations rail (its fetch starts early when the queue reaches it, unless its own
   observer started it first), home rails and tiles, collection and search grids; (2) gallery photos behind "Show all".
   How: the real <img> gets loading=eager and fetchpriority=low, so the browser picks the same srcset candidate it would
   have picked on scroll, and the later swipe or scroll reuses that file (no second download). A photo that could pick a
   file already on the page, or one downloading, is left to the browser (WebKit fetches such a lazy copy a second time:
   the footer logo, a card shown in two rails).
   Skipped: photos without a src (the lightbox and mega-menu photos keep data-src until opened), card hover photos (they
   load when the card is hovered), anything display:none, closed dialogs, drawers and <details>, the cart drawer,
   iframes and videos.
   Limits: off with Save-Data or a 2G connection; at most 3 MB of photos per page on phones and touch screens, 6 MB
   elsewhere (resource-timing bytes, else an estimate; the two in flight when it is reached finish); paused while the
   tab is hidden; stops on pagehide (resumes on a back-forward cache restore). New content (filtered grids,
   recommendations, reviews, pairs) is picked up by one childList MutationObserver on <body> that only marks the list
   stale; the rescan runs in the next idle callback.
   Debug: console.debug lines prefixed [at-warmup]; window.atWarmup holds the counts, the order (log) and what the
   budget skipped. */
(function () {
  'use strict';
  var conn = navigator.connection;
  if (conn && (conn.saveData || /(^|-)2g$/.test(conn.effectiveType || ''))) {
    console.debug('[at-warmup] off: ' + (conn.saveData ? 'Save-Data' : conn.effectiveType));
    return;
  }

  var small = matchMedia('(max-width: 800px)');
  var BUDGET = matchMedia('(hover: none)').matches || small.matches ? 3e6 : 6e6;
  var SKIP_IN = 'cart-drawer, menu-drawer, product-modal, .product-media-modal, dialog:not([open]), details:not([open]), [inert], template';
  var LIST = 'img[loading="lazy"], img.hc-media__thumb[data-at-photo="loading"], product-recommendations';

  var queue = [];
  var inflight = [];
  var queued = new WeakSet();
  var started = [];
  var kept = [];
  var bytes = new Map();
  var state = (window.atWarmup = { on: false, idle: false, started: 0, done: 0, bytes: 0, budget: BUDGET, skipped: [], log: [] });
  var stale = true;
  var stopped = false;
  var spent = false;
  var planned = false;
  var lcpEl = null;
  var lcpObs = null;
  var painted = false;
  var booted = false;
  try {
    if (PerformanceObserver.supportedEntryTypes.indexOf('largest-contentful-paint') < 0) throw 0;
    lcpObs = new PerformanceObserver(function (list) {
      var e = list.getEntries();
      if (!e.length) return;
      lcpEl = e[e.length - 1].element;
      painted = true;
      boot();
    });
    lcpObs.observe({ type: 'largest-contentful-paint', buffered: true });
  } catch (e) {
    painted = true; // no LCP entries (Safari): DOMContentLoaded, then screenBusy waits for the first screen's photos
  }

  var idle = window.requestIdleCallback
    ? function (fn) { requestIdleCallback(fn); }
    : function (fn) { Promise.resolve().then(fn); };

  function visible(el) {
    return el.checkVisibility ? el.checkVisibility() : el.getClientRects().length > 0;
  }

  // at-reviews.js's rule for the uncropped copy of a HelpfulCrowd thumbnail (keep the two in step).
  function largeSrc(src) {
    try {
      var u = new URL(src, location.href);
      if (/(^|\.)helpfulcrowdcdn\.com$/.test(u.hostname) && /\.thumb\.jpe?g$/i.test(u.pathname)) {
        u.pathname = u.pathname.replace(/\.thumb\.(jpe?g)$/i, '.large.$1');
      } else if (u.hostname === 'res.cloudinary.com' && u.pathname.indexOf('/helpfulcrowd/image/upload/') === 0) {
        u.pathname = u.pathname.replace(/\/c_fill,g_auto,h_\d+,w_\d+\//, '/w_1100/');
      }
      return u.href === src ? null : u.href;
    } catch (e) {
      return null;
    }
  }

  function gallery() {
    return document.querySelector('media-gallery .product__media-list');
  }

  function loaded(img) {
    return img.complete && !!img.currentSrc && img.naturalWidth > 0;
  }

  // Two <img> tags with the same sources and sizes pick the same file.
  function keyOf(el) {
    var src = el.parentNode && el.parentNode.tagName === 'PICTURE' ? el.parentNode.querySelector('source') : null;
    return (src ? src.getAttribute('srcset') + '|' + src.getAttribute('media') + '|' : '') + (el.getAttribute('srcset') || el.getAttribute('src')) + '|' + el.getAttribute('sizes');
  }

  // Every file the <img> could pick, as absolute URLs.
  function urls(el) {
    var list = (el.getAttribute('srcset') || '').split(',').map(function (c) { return c.trim().split(/\s+/)[0]; });
    list.push(el.getAttribute('src') || '');
    return list.filter(Boolean).map(function (u) { return new URL(u, location.href).href; });
  }

  // A recommendations rail nobody has asked to load yet (empty, no request started by its observer or by us).
  function recsDue(el) {
    return el.isConnected && el.matches(':empty') && !el.recsLoad && typeof el.loadRecommendations === 'function' && !!el.dataset.url && visible(el);
  }

  // null = leave it (not now; asked again on the next scan)
  function classify(el, g, have) {
    if (el.tagName === 'PRODUCT-RECOMMENDATIONS') {
      if (!recsDue(el)) return null;
      return { el: el, kind: 'recs', tier: 1, key: 'recs', files: [] };
    }
    // Card hover photos load when the card is hovered (Product cards in BUILD-NOTES), never ahead of it.
    if (el.closest(SKIP_IN) || el.classList.contains('at-card-alt')) return null;
    if (el.classList.contains('hc-media__thumb')) {
      var big = largeSrc(el.getAttribute('src') || '');
      if (!big || !visible(el)) return null;
      return { el: el, kind: 'url', tier: 1, url: big, key: big, files: [big] };
    }
    if (!el.getAttribute('src') && !el.getAttribute('srcset')) return null;
    if (loaded(el)) return null;
    var key = keyOf(el);
    // A lazy copy of a photo already on the page (the footer logo, a card in two rails) is left to the browser: WebKit
    // fetches such a copy again instead of reusing the first, and a warm-up would only bring that forward.
    var files = urls(el);
    if (files.some(function (u) { return have.has(u); })) return null;
    if (g && g.contains(el)) {
      var hero = g.classList.contains('is-hero') || (g.classList.contains('is-hero-mobile') && small.matches);
      return { el: el, kind: 'img', tier: visible(el) || hero ? 0 : 2, key: key, files: files };
    }
    if (!visible(el)) return null;
    return { el: el, kind: 'img', tier: 1, key: key, files: files };
  }

  function scan() {
    stale = false;
    var g = gallery();
    var all = document.querySelectorAll(LIST);
    var order = new Map();
    var have = new Set();
    for (var j = 0; j < document.images.length; j++) {
      if (loaded(document.images[j])) have.add(document.images[j].currentSrc);
    }
    for (var i = 0; i < all.length; i++) {
      var el = all[i];
      order.set(el, i);
      if (queued.has(el)) continue;
      var it = classify(el, g, have);
      if (!it) continue;
      queued.add(el);
      queue.push(it);
    }
    queue = queue.filter(function (it) {
      if (!it.el.isConnected) return false;
      it.idx = order.has(it.el) ? order.get(it.el) : 1e9;
      return true;
    });
    // A photo removed before it finished (a filtered grid replaced) never fires load: free its slot.
    inflight = inflight.filter(function (r) { return r.it.kind !== 'img' || r.it.el.isConnected; });
  }

  // How far an item is from the screen, down or across (a rail's fifth card is further than the next rail's first). In a
  // content-visibility:auto home section that is skipped (off screen) the section's box stands in: asking for the
  // photo's box would lay the section out.
  function distance(it) {
    var el = it.el;
    if (el.checkVisibility && !el.checkVisibility({ contentVisibilityAuto: true })) el = el.closest('main > .shopify-section') || el;
    var r = el.getBoundingClientRect();
    var dy = r.top > innerHeight ? r.top - innerHeight : r.bottom < 0 ? -r.bottom : 0;
    var dx = r.left > innerWidth ? r.left - innerWidth : r.right < 0 ? -r.right : 0;
    return dy + dx;
  }

  function pick() {
    var busy = new Set();
    inflight.forEach(function (r) {
      busy.add(r.it.key);
      r.it.files.forEach(function (u) { busy.add(u); });
    });
    var best = null;
    var bd = 0;
    for (var i = 0; i < queue.length; i++) {
      var it = queue[i];
      if (busy.has(it.key) || it.files.some(function (u) { return busy.has(u); })) continue;
      if (best && it.tier > best.tier) continue;
      var d = it.tier === 0 ? 0 : distance(it);
      if (!best || it.tier < best.tier || d < bd || (d === bd && it.idx < best.idx)) {
        best = it;
        bd = d;
      }
    }
    return best;
  }

  function used() {
    var sum = 0;
    started.forEach(function (s) {
      var b = s.url && bytes.get(s.url);
      sum += b !== undefined ? b : s.est || 0;
    });
    state.bytes = sum;
    return sum;
  }

  function label(it) {
    var el = it.el;
    return it.url || (el.currentSrc || el.getAttribute('src') || el.tagName.toLowerCase()).replace(/^https?:\/\/[^/]+/, '').split('?')[0];
  }

  function finish(rec) {
    if (rec.done) return;
    rec.done = true;
    inflight.splice(inflight.indexOf(rec), 1);
    if (!rec.skip) {
      var img = rec.img || rec.it.el;
      started.push({ url: img.currentSrc || rec.it.url, est: Math.round((img.naturalWidth || 0) * (img.naturalHeight || 0) * 0.15) });
      state.done++;
      state.log.push(label(rec.it));
    }
    if (spent) announceIdle();
    schedule();
  }

  function start(it) {
    var el = it.el;
    if (!state.firstAt) state.firstAt = Math.round(performance.now());
    if (it.kind === 'recs') {
      // Checked again: the shopper may have scrolled down while it waited in the queue, and the rail's own observer
      // loaded it (loadRecommendations is idempotent too, so this is belt and braces).
      if (!recsDue(el)) return;
      state.started++;
      el.loadRecommendations(el.dataset.productId);
      state.log.push('recommendations');
      return;
    }
    var rec = { it: it };
    inflight.push(rec);
    var done = function () { finish(rec); };
    if (it.kind === 'url') {
      state.started++;
      // Kept for the page's life: HelpfulCrowd's CDN sends no cache lifetime, so Chrome reuses the file for
      // at-reviews.js's own new Image() only while this one is still referenced (else it downloads it again).
      var im = (rec.img = new Image());
      kept.push(im);
      im.fetchPriority = 'low';
      im.onload = im.onerror = done;
      im.src = it.url;
      return;
    }
    // Another <img> may have loaded one of its files since the scan (the same photo in two rails): leave this copy to
    // the browser, as in classify.
    var have = new Set();
    for (var j = 0; j < document.images.length; j++) {
      if (document.images[j] !== el && loaded(document.images[j])) have.add(document.images[j].currentSrc);
    }
    if (!el.isConnected || loaded(el) || it.files.some(function (u) { return have.has(u); })) {
      rec.skip = true;
      return done();
    }
    state.started++;
    el.addEventListener('load', done, { once: true });
    el.addEventListener('error', done, { once: true });
    el.fetchPriority = 'low';
    el.loading = 'eager';
    if (loaded(el)) done();
  }

  function out() {
    spent = true;
    state.skipped = queue.map(label);
    console.debug('[at-warmup] budget reached (' + Math.round(used() / 1024) + ' KB of ' + Math.round(BUDGET / 1024) + ' KB); skipped ' + queue.length + ':', state.skipped);
    queue = [];
    announceIdle();
    stop();
  }

  // Klaviyo can take its idle turn once the first screen is in and at most two warm-up photos remain (including
  // those in flight). A budget stop leaves only those in flight; a pause for an on-screen photo is not idle.
  function announceIdle() {
    // Next-page work waits for the last photo too; Klaviyo keeps its earlier, two-remaining signal below.
    if (!queue.length && !inflight.length && !state.idle) {
      state.idle = true;
      document.dispatchEvent(new CustomEvent('at:warmup-done'));
    }
    if (queue.length + inflight.length > 2 || state.idleAnnounced) return;
    state.idleAnnounced = true;
    window.dispatchEvent(new CustomEvent('at:warmup-idle', { detail: { remaining: queue.length + inflight.length } }));
  }

  // What she can see comes first: while a photo on the screen is still arriving (the first screen, or where she has
  // just scrolled or swiped to) nothing new starts, and the queue moves on when the last of them lands. (Not "any photo
  // in flight": Safari gives a deferred lazy photo a currentSrc, and Chrome leaves a lazy copy of a cached file
  // incomplete, so neither can tell a download from a photo nobody asked for.)
  var watched = new WeakSet();
  function screenBusy() {
    var busy = false;
    var imgs = document.images;
    for (var i = 0; i < imgs.length; i++) {
      var img = imgs[i];
      if (img.complete || (!img.getAttribute('src') && !img.getAttribute('srcset'))) continue;
      if (img.checkVisibility ? !img.checkVisibility({ contentVisibilityAuto: true, visibilityProperty: true }) : !img.getClientRects().length) continue;
      if (img.closest(SKIP_IN)) continue;
      var r = img.getBoundingClientRect();
      if (r.width < 2 || r.height < 2 || r.bottom <= 0 || r.top >= innerHeight || r.right <= 0 || r.left >= innerWidth) continue;
      busy = true;
      if (!watched.has(img)) {
        watched.add(img);
        img.addEventListener('load', schedule, { once: true });
        img.addEventListener('error', schedule, { once: true });
      }
    }
    return busy;
  }

  function pump() {
    if (stopped || document.visibilityState === 'hidden') return;
    if (screenBusy()) return;
    if (stale) scan();
    // One photo at a time until window load, two after.
    while (inflight.length < (document.readyState === 'complete' ? 2 : 1)) {
      if (used() >= BUDGET) return queue.length ? out() : undefined;
      var it = pick();
      if (!it) break;
      queue.splice(queue.indexOf(it), 1);
      start(it);
    }
    announceIdle();
    if (!queue.length && !inflight.length && state.started && !state.idleLogged) {
      state.idleLogged = true;
      console.debug('[at-warmup] ' + state.done + ' photos warmed, ' + Math.round(used() / 1024) + ' KB; watching for new content');
    }
  }

  function schedule() {
    if (planned || stopped) return;
    state.idle = false;
    planned = true;
    idle(function () {
      planned = false;
      pump();
    });
  }

  var mo = new MutationObserver(function () {
    stale = true;
    state.idleLogged = false;
    schedule();
  });
  var po = null;
  try {
    po = new PerformanceObserver(function (list) {
      list.getEntries().forEach(function (e) {
        if (e.initiatorType === 'img' || e.initiatorType === 'image' || e.initiatorType === 'other') {
          var b = e.transferSize || e.encodedBodySize;
          if (b) bytes.set(e.name, b);
        }
      });
    });
  } catch (e) {}

  function stop() {
    stopped = true;
    mo.disconnect();
    if (po) po.disconnect();
  }

  function go() {
    if (spent) return;
    stopped = false;
    stale = true;
    state.on = true;
    if (lcpObs) lcpObs.disconnect();
    mo.observe(document.body, { childList: true, subtree: true });
    if (po) po.observe({ type: 'resource', buffered: true });
    schedule();
  }

  // A wait on an on-screen photo ends with its load; after a scroll the screen holds other photos, so look again.
  addEventListener('scrollend', schedule, { capture: true, passive: true });
  document.addEventListener('visibilitychange', function () {
    if (document.visibilityState === 'visible') schedule();
  });
  addEventListener('pagehide', stop);
  addEventListener('pageshow', function (e) {
    if (e.persisted && state.on) go();
  });

  // Start: the whole document has arrived (DOMContentLoaded, so every photo is in the list), the page has painted its
  // largest element (an LCP entry, where the browser reports them) and, if that is a photo, it has landed; then the
  // first idle callback. Window load is not waited for: on phones third-party scripts hold it back (hosted, 16-17 s).
  // Window load also starts it, whichever comes first. Until load, one photo at a time, so the load event (where
  // HelpfulCrowd, 'Pairs well with' and the store's app scripts start) is held back by one photo at most.
  function boot() {
    if (booted || spent || document.readyState === 'loading') return;
    if (document.readyState !== 'complete' && !painted) return;
    if (lcpEl && lcpEl.tagName === 'IMG' && !lcpEl.complete) {
      lcpEl.addEventListener('load', boot, { once: true });
      lcpEl.addEventListener('error', boot, { once: true });
      return;
    }
    booted = true;
    idle(go);
  }
  document.addEventListener('DOMContentLoaded', boot);
  addEventListener('load', function () {
    boot();
    schedule();
  });
  boot();
})();
